/**
 * Remote playback: AirPlay (Apple devices) and Chromecast.
 *
 * Chromecast comes from @silvermine/videojs-chromecast, which registers its
 * own tech + control-bar button; this module only supplies the AirPlay button
 * (WebKit exposes no built-in one) and the shared "playing on <device>"
 * overlay that covers both.
 *
 * AirPlay uses the WebKit-specific API rather than the Remote Playback API,
 * because Safari — the only browser that can reach an Apple TV / AirPlay
 * receiver — implements the former reliably.
 */

import type Player from 'video.js/dist/types/player'

const SVG_ATTRS =
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"'

/** AirPlay glyph: display + upward triangle */
const ICON_AIRPLAY =
  `<svg ${SVG_ATTRS}><path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"/>` +
  `<polygon points="12 15 17 21 7 21 12 15" fill="currentColor"/></svg>`

const ICON_CAST_BIG =
  `<svg ${SVG_ATTRS}><path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6"/><line x1="2" y1="20" x2="2.01" y2="20"/></svg>`

/** Safari-only extensions to HTMLVideoElement for AirPlay. */
interface AirPlayVideo extends HTMLVideoElement {
  webkitShowPlaybackTargetPicker?: () => void
  webkitCurrentPlaybackTargetIsWireless?: boolean
}

export interface CastHandle {
  dispose(): void
}

export interface CastControlsContext {
  /**
   * A remote session ended — deliberately, or because the receiver vanished
   * (TV switched off, network dropped). The Chromecast *tech* stays selected
   * with a dead session, so the controller must rebuild on the local tech or
   * the viewer is left staring at a black player.
   */
  onRemoteSessionEnded: () => void
}

export function mountCastControls(player: Player, ctx: CastControlsContext): CastHandle {
  const playerEl = player.el() as HTMLElement
  const controlBar = playerEl.querySelector('.vjs-control-bar')
  const video = playerEl.querySelector('video') as AirPlayVideo | null
  if (!controlBar || !video) return { dispose: () => undefined }

  const cleanups: (() => void)[] = []

  // ── "playing on <device>" overlay (shared by AirPlay + Chromecast) ────
  const overlay = document.createElement('div')
  overlay.className = 'lp-cast-overlay'
  const icon = document.createElement('div')
  icon.className = 'lp-cast-overlay-icon'
  icon.innerHTML = ICON_CAST_BIG // static markup
  const text = document.createElement('div')
  text.className = 'lp-cast-overlay-text'

  // Explicit way back — without it the only exit is the device's own remote.
  const stopButton = document.createElement('button')
  stopButton.type = 'button'
  stopButton.className = 'lp-cast-stop'
  overlay.append(icon, text, stopButton)
  playerEl.appendChild(overlay)

  let stopAction: (() => void) | null = null
  stopButton.addEventListener('click', (ev) => {
    ev.stopPropagation()
    stopAction?.()
  })

  const showOverlay = (message: string | null, stopLabel = '', onStop: (() => void) | null = null): void => {
    if (message) text.textContent = message
    stopButton.textContent = stopLabel
    stopButton.classList.toggle('lp-hidden', !onStop)
    stopAction = onStop
    overlay.classList.toggle('show', message !== null)
    playerEl.classList.toggle('lp-casting', message !== null)
  }

  // ── AirPlay ───────────────────────────────────────────────────────────
  if (typeof video.webkitShowPlaybackTargetPicker === 'function') {
    // Permit AirPlay for this element (Safari honours the attribute).
    video.setAttribute('x-webkit-airplay', 'allow')

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'vjs-control vjs-button lp-airplay-button lp-hidden'
    button.setAttribute('aria-label', 'AirPlay')
    button.innerHTML = ICON_AIRPLAY // static markup
    button.addEventListener('click', (ev) => {
      ev.stopPropagation()
      try {
        video.webkitShowPlaybackTargetPicker?.()
      } catch {
        /* picker unavailable */
      }
    })

    // Sit just before the settings gear, next to the other remote controls.
    const gear = controlBar.querySelector('.lp-settings-button')
    controlBar.insertBefore(button, gear)

    // Safari reveals the button once a receiver is actually on the network.
    let sawAvailabilityEvent = false
    const onAvailability = (ev: Event): void => {
      sawAvailabilityEvent = true
      const available = (ev as Event & { availability?: string }).availability === 'available'
      button.classList.toggle('lp-hidden', !available)
    }
    video.addEventListener('webkitplaybacktargetavailabilitychanged', onAvailability)

    // Safety net: the availability event can fire before our listener is
    // attached (we mount on player ready, after the element exists). If we
    // never hear from it, show the button anyway — Safari's own picker will
    // report if there is nothing to send to. Silence is not "no devices".
    const fallbackReveal = setTimeout(() => {
      if (!sawAvailabilityEvent) button.classList.remove('lp-hidden')
    }, 3000)
    cleanups.push(() => clearTimeout(fallbackReveal))

    /**
     * WebKit exposes no "disconnect" call. Toggling disableRemotePlayback
     * hands the route back to the browser on current Safari; if the video is
     * still wireless a moment later we fall back to reopening the picker,
     * where selecting this Mac ends the session.
     */
    const stopAirPlay = (): void => {
      try {
        const el = video as HTMLVideoElement & { disableRemotePlayback?: boolean }
        el.disableRemotePlayback = true
        setTimeout(() => {
          el.disableRemotePlayback = false
          if (video.webkitCurrentPlaybackTargetIsWireless === true) {
            video.webkitShowPlaybackTargetPicker?.()
          }
        }, 500)
      } catch {
        video.webkitShowPlaybackTargetPicker?.()
      }
    }

    let wasWireless = false
    const onWirelessChange = (): void => {
      const active = video.webkitCurrentPlaybackTargetIsWireless === true
      button.classList.toggle('active', active)
      showOverlay(active ? 'Playing on AirPlay' : null, 'Stop AirPlay', active ? stopAirPlay : null)
      if (wasWireless && !active) ctx.onRemoteSessionEnded()
      wasWireless = active
    }
    video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWirelessChange)

    cleanups.push(() => {
      video.removeEventListener('webkitplaybacktargetavailabilitychanged', onAvailability)
      video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWirelessChange)
      button.remove()
    })
  }

  // ── Chromecast (plugin-driven; we only mirror its connected state) ────
  const stopChromecast = (): void => {
    try {
      const mgr = (player as unknown as {
        chromecastSessionManager?: { getCastContext?: () => { endCurrentSession?: (stop: boolean) => void } }
      }).chromecastSessionManager
      mgr?.getCastContext?.()?.endCurrentSession?.(true)
    } catch {
      /* session already gone */
    }
  }

  const onCastConnected = (): void =>
    showOverlay('Casting to Chromecast', 'Stop casting', stopChromecast)
  const onCastDisconnected = (): void => {
    showOverlay(null)
    ctx.onRemoteSessionEnded()
  }

  player.on('chromecastConnected', onCastConnected)
  player.on('chromecastDisconnected', onCastDisconnected)
  cleanups.push(() => {
    player.off('chromecastConnected', onCastConnected)
    player.off('chromecastDisconnected', onCastDisconnected)
  })

  return {
    dispose: () => {
      for (const fn of cleanups) fn()
      overlay.remove()
      playerEl.classList.remove('lp-casting')
    },
  }
}
